// src/App.jsx
// Root app — drives the full auth flow:
//   Splash → check device → Welcome (if new) or Dashboard (if known)
//   Welcome → Login or Register
//   Login → OTP → Dashboard
//   Register → verify email OTP → fill company form → Dashboard

import "./styles/global.css";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { RouterProvider }         from "./contexts/RouterContext";
import { ToastProvider }          from "./contexts/ToastContext";

import SplashScreen   from "./pages/auth/SplashScreen";
import WelcomeScreen  from "./pages/auth/WelcomeScreen";
import LoginScreen    from "./pages/auth/LoginScreen";
import RegisterScreen from "./pages/auth/RegisterScreen";
import LockScreen     from "./pages/auth/LockScreen";
import AppShell       from "./components/layout/AppShell";

import { useState } from "react";

// Possible screens in the auth funnel
const SCREEN = {
  SPLASH:    "splash",
  WELCOME:   "welcome",
  LOGIN:     "login",
  REGISTER:  "register",
};

// CM_Status values that lock the app behind the activation screen
const LOCKED_STATUSES = [2, 4]; // 2 = Demo Expired, 4 = Activation Expired

function AppRouter() {
  const { company, loading } = useAuth();
  const [screen, setScreen] = useState(SCREEN.SPLASH);

  // While AuthContext re-hydrates from sessionStorage, show nothing
  if (loading) return null;

  // Already authenticated → check license status before granting access
  if (company) {
    const isLocked = LOCKED_STATUSES.includes(Number(company.CM_Status));
    return isLocked ? <LockScreen /> : <AppShell />;
  }

  // Auth funnel
  switch (screen) {
    case SCREEN.SPLASH:
      return (
        <SplashScreen
          onRegistered={() => setScreen(SCREEN.WELCOME)}   // device NOT in DB
          onLoggedIn={null}                                 // device IS in DB → AuthContext sets company → re-render
        />
      );

    case SCREEN.WELCOME:
      return (
        <WelcomeScreen
          onLogin={()    => setScreen(SCREEN.LOGIN)}
          onRegister={()  => setScreen(SCREEN.REGISTER)}
        />
      );

    case SCREEN.LOGIN:
      return (
        <LoginScreen
          onBack={() => setScreen(SCREEN.WELCOME)}
        />
      );

    case SCREEN.REGISTER:
      return (
        <RegisterScreen
          onBack={() => setScreen(SCREEN.WELCOME)}
        />
      );

    default:
      return <SplashScreen onRegistered={() => setScreen(SCREEN.WELCOME)} onLoggedIn={null} />;
  }
}

export default function App() {
  return (
    <AuthProvider>
      <RouterProvider>
        <ToastProvider>
          <AppRouter />
        </ToastProvider>
      </RouterProvider>
    </AuthProvider>
  );
}
